import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useSelector } from "react-redux";
import { FiChevronRight, FiFileText, FiShoppingBag } from "react-icons/fi";
import { RxFile } from "react-icons/rx";
import { BsFileEarmarkArrowDown } from "react-icons/bs";
import { BiBookAlt, BiWallet } from "react-icons/bi";
import { PiSidebarSimpleDuotone } from "react-icons/pi";
import { LuBookOpen } from "react-icons/lu";
import { ImStack } from "react-icons/im";
import { FaFileInvoice, FaGasPump } from "react-icons/fa6";
import CustomList14 from "./CustomList14";
import CustomList from "./CustomList";
import { FaTrashAlt } from "react-icons/fa";

const TransReportList = React.memo(({ close, path }) => {
  const [expandedSections, setExpandedSections] = useState(new Set());
  const navigate = useNavigate();
  const { emp_data, user_type } = useSelector((state) => state.other);

  const transactionsMenu = useMemo(
    () => [
      {
        id: "vehicle-reports",
        title: "Vehicle Movement",
        icon: <FiShoppingBag size={15} color="#6e82a5" />,
        children: [
          {
            title: "Godown Register",
            route: "/transactionReport/VehicleMovement/GodownRegister",
            icon: <FaFileInvoice size={15} color="#6e82a5" />,
            permission: "VENDOR_PO_PERMISSION",
          },
          {
            title: "Godown Stock Recon",
            route: "/transactionReport/VehicleMovement/GodownStockRecon",
            icon: <FaGasPump size={15} color="#6e82a5" />,
            permission: "CP_PERMISSION",
          },
        ],
      },
    ],
    []
  );

  const transactionReportsMenu = useMemo(
    () => [
      {
        title: "Delete Records Logs",
        icon: <FaTrashAlt color="#6e82a5" size={13} />,
        route: "/transactionReport/DeleteRecordsLogs",
        activePath: "/transactionReport/DeleteRecordsLogs",
        permission: "DAY_END_PERMISSION", // remaining
      },
      {
        title: "Purchase Report",
        icon: <ImStack color="#6e82a5" size={15} />,
        route: "/transactionReport/PurchaseReport",
        permission: "PURCHASE_REPORT_PERMISSION",
      },
      {
        title: "Sales Report",
        icon: <RxFile color="#6e82a5" size={15} />,
        route: "/transactionReport/SalesReport",
        permission: "SALES_REPORT_PERMISSION",
      },
      {
        title: "NC/DBC/RC/TV Report",
        icon: <FiFileText color="#6e82a5" size={15} />,
        route: "/transactionReport/NDRTReport",
        permission: "NCDBC_REPORT_PERMISSION",
      },
      {
        title: "Stock Report",
        icon: <RxFile color="#6e82a5" size={15} />,
        route: "/transactionReport/StockReport",
        permission: "STOCK_REPORT_PERMISSION",
      },
      {
        title: "Bank Book",
        icon: <BiBookAlt color="#6e82a5" size={15} />,
        route: "/transactionReport/BankBook",
        permission: "BANK_BOOK_PERMISSION",
      },
      {
        title: "Ledger",
        icon: <PiSidebarSimpleDuotone color="#6e82a5" size={15} />,
        route: "/transactionReport/Ledger",
        permission: "LEDGER_REPORT_PERMISSION",
      },
      {
        title: "Receivables",
        icon: <BsFileEarmarkArrowDown color="#6e82a5" size={15} />,
        route: "/transactionReport/Receivables",
        permission: "RECEIVABLE_PERMISSION",
      },
      {
        title: "Payables",
        icon: <BiWallet color="#6e82a5" size={15} />,
        route: "/transactionReport/Payables",
        permission: "PAYABLE_PERMISSION",
      },
      {
        title: "Payment & Receipts",
        icon: <BiWallet color="#6e82a5" size={15} />,
        route: "/transactionReport/PaymentReceipts",
        permission: "PAYMENT_RECEIPT_REPORT_PERMISSION",
      },
    ],
    []
  );

  const hasPermission = useMemo(
    () =>
      (permissions = []) => {
        return (
          user_type === "admin" || permissions.some((perm) => emp_data?.[perm])
        );
      },
    [user_type, emp_data]
  );

  const hasAnyTransactionReportPermission = useMemo(
    () => hasPermission(transactionReportsMenu.map((v) => v.permission)),
    [hasPermission, transactionReportsMenu]
  );

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const handleNavigation = (route) => {
    navigate(route);
    close();
  };

  const isExpanded = (sectionId) => expandedSections.has(sectionId);

  return (
    <>
      {hasAnyTransactionReportPermission && (
        <div
          className="list-main mt10"
          onClick={() => toggleSection("transactions")}
        >
          <div className="list-icon">
            <LuBookOpen color="#6e82a5" size={18} />
          </div>
          <div className="list-title fs15">Transaction Reports</div>
          <div className="list-arrow">
            <FiChevronRight
              size={18}
              className={
                isExpanded("transactions") ? "rotate-down" : "rotate-right"
              }
            />
          </div>
        </div>
      )}

      <AnimatePresence initial={false}>
        {isExpanded("transactions") && (
          <motion.section
            style={{ marginLeft: 15, overflow: "hidden" }}
            key="transactions-content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { opacity: 1, height: "auto" },
              collapsed: { opacity: 0, height: 0 },
            }}
            transition={{ duration: 1, ease: [0.04, 0.62, 0.23, 0.98] }}
          >
            {/* Vehicle Reports Module */}
            {transactionsMenu.map((menuItem) => (
              <React.Fragment key={menuItem.id}>
                <div
                  className="list-main"
                  onClick={() => toggleSection(menuItem.id)}
                >
                  <div className="list-icon">{menuItem.icon}</div>
                  <div className="list-title fs14">{menuItem.title}</div>
                  <div className="list-arrow">
                    <FiChevronRight
                      size={18}
                      className={
                        isExpanded(menuItem.id) ? "rotate-down" : "rotate-right"
                      }
                    />
                  </div>
                </div>

                <AnimatePresence initial={false}>
                  {isExpanded(menuItem.id) && (
                    <motion.div
                      style={{ marginLeft: 15, overflow: "hidden" }}
                      key={`${menuItem.id}-content`}
                      initial="collapsed"
                      animate="open"
                      exit="collapsed"
                      variants={{
                        open: { opacity: 1, height: "auto" },
                        collapsed: { opacity: 0, height: 0 },
                      }}
                      transition={{
                        duration: 1,
                        ease: [0.04, 0.62, 0.23, 0.98],
                      }}
                    >
                      {menuItem.children?.map((child, index) => (
                        <React.Fragment key={`${menuItem.id}-child-${index}`}>
                          {hasPermission([child.permission]) && (
                            <CustomList
                              icon={child.icon}
                              title={child.title}
                              style={{
                                backgroundColor:
                                  path === child.route ? "white" : "",
                              }}
                              type={child.permission}
                              onClick={() => handleNavigation(child.route)}
                            />
                          )}
                        </React.Fragment>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </React.Fragment>
            ))}

            {/* Transaction Reports Menu Items */}
            {transactionReportsMenu.map((item, index) => (
              <React.Fragment key={`report-${index}`}>
                {hasPermission([item.permission]) && (
                  <CustomList14
                    title={item.title}
                    style={{
                      backgroundColor: path === item.route ? "white" : "",
                    }}
                    type={item.permission}
                    onClick={() => handleNavigation(item.route)}
                    icon={item.icon}
                  />
                )}
              </React.Fragment>
            ))}
          </motion.section>
        )}
      </AnimatePresence>
    </>
  );
});

TransReportList.displayName = "TransReportList";

export default TransReportList;
// import React, { useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import { AnimatePresence, motion } from "framer-motion";
// import CustomList14 from "./CustomList14";
// import { FiChevronRight, FiFileText, FiShoppingBag } from "react-icons/fi";
// import { RxFile } from "react-icons/rx";
// import { BsFileEarmarkArrowDown } from "react-icons/bs";
// import { BiBookAlt, BiWallet } from "react-icons/bi";
// import { PiSidebarSimpleDuotone } from "react-icons/pi";
// import { LuBookOpen } from "react-icons/lu";
// import { useSelector } from "react-redux";
// import { ImStack } from "react-icons/im";

// import { get_purchase_report_list } from "../../screens/transactionReport/transactionReportSlice";
// import store from "../../redux/store";
// import { FaFileInvoice, FaGasPump } from "react-icons/fa6";
// import CustomList from "./CustomList";

// const TransReportList = React.memo(({ close, path }) => {
//   const [focused, setFocused] = useState([]);
//   const navigate = useNavigate();
//   const { emp_data, user_type } = useSelector((state) => state.other);

//   const transactionsMenu = [
//     {
//       title: "Vehicle Reports",
//       icon: <FiShoppingBag size={15} color="#6e82a5" />,
//       children: [
//         {
//           title: "Vehicle Tracking",
//           route: "/transactionReport/VehicleTracking",
//           icon: <FaFileInvoice size={15} color="#6e82a5" />,
//           permission: "VENDOR_PO_PERMISSION",
//           activePath: "/transactionReport/VehicleTracking",
//         },
//         {
//           title: "Day End Report",
//           route: "/transactionReport/DayEndReport",
//           icon: <FaGasPump size={15} color="#6e82a5" />,
//           permission: "CP_PERMISSION",
//           activePath: "/transactionReport/DayEndReport",
//         },
//       ],
//     },
//   ];
//   const transactionReportsMenu = [
//     {
//       title: "Purchase Report",
//       // icon: <RxFile color="#6e82a5" size={15} />,
//       icon: <ImStack color="#6e82a5" size={15} />,
//       route: "/transactionReport/PurchaseReport",
//       activePath: "/transactionReport/PurchaseReport",
//       permission: "PURCHASE_REPORT_PERMISSION",
//     },
//     {
//       title: "Sales Report",
//       icon: <RxFile color="#6e82a5" size={15} />,
//       route: "/transactionReport/SalesReport",
//       activePath: "/transactionReport/SalesReport",
//       permission: "SALES_REPORT_PERMISSION",
//     },
//     {
//       title: "NC/DBC/RC/TV Report",
//       icon: <FiFileText color="#6e82a5" size={15} />,
//       route: "/transactionReport/NDRTReport",
//       activePath: "/transactionReport/NDRTReport",
//       permission: "NCDBC_REPORT_PERMISSION",
//     },
//     {
//       title: "Stock Report",
//       icon: <RxFile color="#6e82a5" size={15} />,
//       route: "/transactionReport/StockReport",
//       activePath: "/transactionReport/StockReport",
//       permission: "STOCK_REPORT_PERMISSION",
//     },
//     {
//       title: "Bank Book",
//       icon: <BiBookAlt color="#6e82a5" size={15} />,
//       route: "/transactionReport/BankBook",
//       activePath: "/transactionReport/BankBook",
//       permission: "BANK_BOOK_PERMISSION",
//     },
//     {
//       title: "Ledger",
//       icon: <PiSidebarSimpleDuotone color="#6e82a5" size={15} />,
//       route: "/transactionReport/Ledger",
//       activePath: "/transactionReport/Ledger",
//       permission: "LEDGER_REPORT_PERMISSION",
//     },
//     {
//       title: "Receivables",
//       icon: <BsFileEarmarkArrowDown color="#6e82a5" size={15} />,
//       route: "/transactionReport/Receivables",
//       activePath: "/transactionReport/Receivables",
//       permission: "RECEIVABLE_PERMISSION",
//     },
//     {
//       title: "Payables",
//       icon: <BiWallet color="#6e82a5" size={15} />,
//       route: "/transactionReport/Payables",
//       activePath: "/transactionReport/Payables",
//       permission: "PAYABLE_PERMISSION",
//     },
//     {
//       title: "Payment & Receipts",
//       icon: <BiWallet color="#6e82a5" size={15} />,
//       route: "/transactionReport/PaymentReceipts",
//       activePath: "/transactionReport/PaymentReceipts",
//       permission: "PAYMENT_RECEIPT_REPORT_PERMISSION",
//     },
//   ];

//   const permission_handle = (permissions = []) => {
//     return (
//       user_type === "admin" || permissions.some((perm) => emp_data?.[perm])
//     );
//   };

//   return (
//     <>
//       {permission_handle(transactionReportsMenu?.map((v) => v?.permission)) && (
//         <div
//           className="list-main mt10"
//           onClick={() =>
//             setFocused((pre) =>
//               pre.find((v) => v == "Transactions")
//                 ? pre.filter((v) => v != "Transactions")
//                 : ["Transactions"]
//             )
//           }
//         >
//           <div className="list-icon">
//             <LuBookOpen color="#6e82a5" size={18} />
//           </div>
//           <div className="list-title fs15">Transaction Reports</div>
//           <div className="list-arrow">
//             <FiChevronRight
//               size={18}
//               className={
//                 focused.includes("Transactions")
//                   ? "rotate-down"
//                   : "rotate-right"
//               }
//             />
//           </div>
//         </div>
//       )}

//       <AnimatePresence initial={false}>
//         {focused.includes("Transactions") && (
//           <motion.section
//             style={{ marginLeft: 15, overflow: "hidden" }}
//             key="content"
//             initial="collapsed"
//             animate="open"
//             exit="collapsed"
//             variants={{
//               open: { opacity: 1, height: "auto" },
//               collapsed: { opacity: 0, height: 0 },
//             }}
//             transition={{ duration: 1, ease: [0.04, 0.62, 0.23, 0.98] }}
//           >
//             {/* vehicle report module */}

//             <>
//               <div
//                 className="list-main"
//                 onClick={() =>
//                   setFocused((pre) => transactionsMenu[0].children)
//                 }
//               >
//                 <div className="list-icon">
//                   <FiShoppingBag size={15} color="#6e82a5" />
//                 </div>
//                 <div className="list-title fs14">Vehicle Reports</div>
//                 <div className="list-arrow">
//                   <FiChevronRight
//                     size={18}
//                     className={
//                       focused.includes("Vehicle Reports")
//                         ? "rotate-down"
//                         : "rotate-right"
//                     }
//                   />
//                 </div>
//               </div>

//               <AnimatePresence>
//                 {focused.includes("Vehicle Reports") && (
//                   <motion.div
//                     style={{ marginLeft: 15, overflow: "hidden" }}
//                     key="content"
//                     initial="collapsed"
//                     animate="open"
//                     exit="collapsed"
//                     variants={{
//                       open: { opacity: 1, height: "auto" },
//                       collapsed: { opacity: 0, height: 0 },
//                     }}
//                     transition={{
//                       duration: 1,
//                       bounce: [0.04, 0.62, 0.23, 0.98],
//                     }}
//                   >
//                     {transactionsMenu[0].children?.map((v, i) => (
//                       <>
//                         {permission_handle([v?.permission]) && (
//                           <CustomList
//                             icon={v?.icon}
//                             key={i}
//                             title={v?.title}
//                             style={{
//                               backgroundColor: path == v?.route ? "white" : "",
//                             }}
//                             type={v?.permission}
//                             onClick={() => {
//                               navigate(v?.route);
//                               close();
//                             }}
//                           />
//                         )}
//                       </>
//                     ))}
//                     {/* <CustomList
//                       title={"Vendor PO"}
//                       style={{
//                         backgroundColor:
//                           path == "/transactions/venderpurchase" ? "white" : "",
//                       }}
//                       onClick={() => {
//                         navigate("/transactions/venderpurchase");
//                         close();
//                       }}
//                     />
//                     <CustomList
//                       title={"Cylinder Purchases"}
//                       style={{
//                         backgroundColor:
//                           path == "/transactions/Cylinder-Purchases"
//                             ? "white"
//                             : "",
//                       }}
//                       onClick={() => {
//                         navigate("/transactions/Cylinder-Purchases");
//                         close();
//                       }}
//                     />
//                     <CustomList
//                       title={
//                         (OMC == 1 && "NFR Purchase") ||
//                         (OMC == 2 && "ARB Purchase") ||
//                         (OMC == 3 && "BLPG Purchase")
//                       }
//                       style={{
//                         backgroundColor:
//                           path == "/transactions/BLPG/ARB/NFR-Purchases"
//                             ? "white"
//                             : "",
//                       }}
//                       onClick={() => {
//                         navigate("/transactions/BLPG/ARB/NFR-Purchases");
//                         close();
//                       }}
//                     />
//                     <CustomList
//                       title={"Other Purchases"}
//                       style={{
//                         backgroundColor:
//                           path == "/transactions/ExpenditureOtherPurchase" ||
//                           path == "/transactions/OtherPurchase"
//                             ? "white"
//                             : "",
//                       }}
//                       onClick={() => {
//                         navigate("/transactions/ExpenditureOtherPurchase");
//                         close();
//                       }}
//                     />
//                     <CustomList
//                       title={"Defective/Pur return"}
//                       style={{
//                         backgroundColor:
//                           path == "/transactions/Defective/Pur/Return"
//                             ? "white"
//                             : "",
//                       }}
//                       onClick={() => {
//                         navigate("/transactions/Defective/Pur/Return");
//                         close();
//                       }}
//                     />
//                        */}
//                   </motion.div>
//                 )}
//               </AnimatePresence>
//             </>
//             {transactionReportsMenu?.map((v, i) => (
//               <>
//                 {permission_handle([v?.permission]) && (
//                   <CustomList14
//                     key={i}
//                     title={v?.title}
//                     style={{
//                       backgroundColor: path == v?.route ? "white" : "",
//                     }}
//                     type={v?.permission}
//                     onClick={() => {
//                       navigate(v?.route);
//                       close();
//                     }}
//                     icon={v?.icon}
//                   />
//                 )}
//               </>
//             ))}
//             {/* <CustomList14
//               style={{
//                 backgroundColor:
//                   path == "/transactionReport/PurchaseReport" ? "white" : "",
//               }}
//               onClick={() => {
//                 navigate("/transactionReport/PurchaseReport");
//                 close();
//               }}
//               title={"Purchase Report"}
//               icon={<RxFile color="#6e82a5" size={15} />}
//             />
//             <CustomList14
//               style={{
//                 backgroundColor:
//                   path == "/transactionReport/SalesReport" ? "white" : "",
//               }}
//               onClick={() => {
//                 navigate("/transactionReport/SalesReport");
//                 close();
//               }}
//               title={"Sales Report"}
//               icon={<RxFile color="#6e82a5" size={15} />}
//             />
//             <CustomList14
//               style={{
//                 backgroundColor:
//                   path == "/transactionReport/NDRTReport" ? "white" : "",
//               }}
//               onClick={() => {
//                 navigate("/transactionReport/NDRTReport");
//                 close();
//               }}
//               title={"NC/DBC/RC/TV Report"}
//               icon={<RxFile color="#6e82a5" size={15} />}
//             />
//             <CustomList14
//               style={{
//                 backgroundColor:
//                   path == "/transactionReport/StockReport" ? "white" : "",
//               }}
//               onClick={() => {
//                 navigate("/transactionReport/StockReport");
//                 close();
//               }}
//               title={"Stock Report"}
//               icon={<RxFile color="#6e82a5" size={15} />}
//             />
//             <CustomList14
//               style={{
//                 backgroundColor:
//                   path == "/transactionReport/BankBook" ? "white" : "",
//               }}
//               onClick={() => {
//                 navigate("/transactionReport/BankBook");
//                 close();
//               }}
//               title={"Bank Book"}
//               icon={<BiBookAlt color="#6e82a5" size={15} />}
//             />
//             <CustomList14
//               style={{
//                 backgroundColor:
//                   path == "/transactionReport/Ledger" ? "white" : "",
//               }}
//               onClick={() => {
//                 navigate("/transactionReport/Ledger");
//                 close();
//               }}
//               title={"Ledger"}
//               icon={<PiSidebarSimpleDuotone color="#6e82a5" size={15} />}
//             />
//             <CustomList14
//               style={{
//                 backgroundColor:
//                   path == "/transactionReport/Receivables" ? "white" : "",
//               }}
//               onClick={() => {
//                 navigate("/transactionReport/Receivables");
//                 close();
//               }}
//               title={"Receivables"}
//               icon={<BsFileEarmarkArrowDown color="#6e82a5" size={15} />}
//             />
//             <CustomList14
//               style={{
//                 backgroundColor:
//                   path == "/transactionReport/Payables" ? "white" : "",
//               }}
//               onClick={() => {
//                 navigate("/transactionReport/Payables");
//                 close();
//               }}
//               title={"Payables"}
//               icon={<BiWallet color="#6e82a5" size={15} />}
//             />
//             <CustomList14
//               style={{
//                 backgroundColor:
//                   path == "/transactionReport/PaymentReceipts" ? "white" : "",
//               }}
//               onClick={() => {
//                 navigate("/transactionReport/PaymentReceipts");
//                 close();
//               }}
//               title={"Payment & Receipts"}
//               icon={<BiWallet color="#6e82a5" size={15} />}
//             /> */}
//           </motion.section>
//         )}
//       </AnimatePresence>
//     </>
//   );
// });

// export default TransReportList;
